<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { makeImgErrorHandler } from '@shared/composables/useImgFallback'
import { PROPERTY_IMAGE_PLACEHOLDER } from '../../browse/constants/propertyPlaceholder'

import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import Galleria from '@uiKit/elements/Galleria.vue'
import photoLibraryIcon from '@assets/images/propertyDetail/photo-library.svg'

// Hero photo (Figma 32590:61540): one image at a time with a "1/6" counter
// bottom-left, page dots bottom-right and "View Gallery" top-right, which
// opens the full-screen viewer. Dots, arrow keys and swipe change the photo.

interface Props {
  images: string[]
}

const props = defineProps<Props>()

const onImgError = makeImgErrorHandler(PROPERTY_IMAGE_PLACEHOLDER)

const activeIndex = ref(0)
const viewerOpen = ref(false)
const count = computed(() => props.images.length)
const hasMany = computed(() => count.value > 1)

watch(
  () => props.images,
  () => {
    activeIndex.value = 0
  },
)

const show = (index: number) => {
  activeIndex.value = (index + count.value) % count.value
}

// Horizontal swipe on touch screens — 40px is a deliberate drag, not a tap.
const SWIPE_MIN_PX = 40
let touchStartX: number | null = null
const onTouchStart = (event: TouchEvent) => {
  touchStartX = event.touches[0]?.clientX ?? null
}
const onTouchEnd = (event: TouchEvent) => {
  const endX = event.changedTouches[0]?.clientX
  if (touchStartX == null || endX == null || !hasMany.value) return
  const delta = endX - touchStartX
  if (Math.abs(delta) >= SWIPE_MIN_PX) show(activeIndex.value + (delta < 0 ? 1 : -1))
  touchStartX = null
}
</script>

<template>
  <Box
    class="pd-gallery"
    tabindex="0"
    aria-roledescription="carousel"
    aria-label="Property photos"
    @keydown.left="hasMany && show(activeIndex - 1)"
    @keydown.right="hasMany && show(activeIndex + 1)"
    @touchstart.passive="onTouchStart"
    @touchend="onTouchEnd"
  >
    <Transition name="pd-gallery-fade">
      <AppImage
        :key="images[activeIndex]"
        :src="images[activeIndex]"
        :alt="`Property photo ${activeIndex + 1} of ${count}`"
        class="pd-gallery__img"
        @error="onImgError"
      />
    </Transition>

    <Box
      is="button"
      type="button"
      align="center"
      gap="1"
      class="pd-gallery__overlay pd-gallery__view"
      @click="viewerOpen = true"
    >
      <AppImage :src="photoLibraryIcon" alt="" aria-hidden="true" class="pd-gallery__view-icon" />
      <Text type="body" weight="semibold" color="inverse" is="span">View Gallery</Text>
    </Box>

    <Box v-if="hasMany" align="center" justify="between" class="pd-gallery__footer">
      <Box align="center" class="pd-gallery__overlay pd-gallery__counter">
        <Text type="micro" weight="bold" color="inverse" is="span" style="letter-spacing: 0">
          {{ activeIndex + 1 }}/{{ count }}
        </Text>
      </Box>
      <Box align="center" gap="3px" class="pd-gallery__overlay pd-gallery__dots">
        <Box
          v-for="(image, index) in images"
          :key="image + index"
          is="button"
          type="button"
          class="pd-gallery__dot"
          :class="{ 'pd-gallery__dot--active': index === activeIndex }"
          :aria-label="`Show photo ${index + 1}`"
          :aria-current="index === activeIndex"
          @click="show(index)"
        />
      </Box>
    </Box>

    <Galleria v-model:visible="viewerOpen" :images="images" />
  </Box>
</template>

<style scoped>
.pd-gallery {
  position: relative;
  width: 100%;
  aspect-ratio: 728 / 420;
  border-radius: 16px;
  overflow: hidden;
  background: var(--p-surface-950);
  outline: none;
}

.pd-gallery:focus-visible {
  box-shadow: 0 0 0 2px var(--p-primary-500);
}

.pd-gallery__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  /* The design dims the photo so the overlay controls read on any image. */
  opacity: 0.6;
}

.pd-gallery-fade-enter-active,
.pd-gallery-fade-leave-active {
  transition: opacity 0.25s ease;
}

.pd-gallery-fade-enter-from,
.pd-gallery-fade-leave-to {
  opacity: 0;
}

.pd-gallery__overlay {
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  border-radius: 8px;
}

.pd-gallery__view {
  position: absolute;
  top: 20px;
  right: 20px;
  height: 34px;
  padding: 4px 12px;
  border: none;
  background: rgba(29, 35, 50, 0.41);
  cursor: pointer;
}

.pd-gallery__view:focus-visible {
  outline: 2px solid var(--p-surface-0);
  outline-offset: 2px;
}

.pd-gallery__view-icon {
  display: block;
  flex-shrink: 0;
}

.pd-gallery__footer {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 20px;
}

.pd-gallery__counter {
  height: 24px;
  padding: 3.5px 8px 4.5px;
  background: rgba(29, 35, 50, 0.2);
}

.pd-gallery__dots {
  height: 24px;
  padding: 8px;
  background: rgba(29, 35, 50, 0.2);
}

.pd-gallery__dot {
  width: 4px;
  height: 4px;
  padding: 0;
  border: none;
  border-radius: 30px;
  background: var(--p-surface-500);
  cursor: pointer;
  transition:
    background 0.2s ease,
    width 0.2s ease;
}

.pd-gallery__dot--active {
  background: var(--p-surface-0);
}

@media (prefers-reduced-motion: reduce) {
  .pd-gallery-fade-enter-active,
  .pd-gallery-fade-leave-active {
    transition: none;
  }
}

@media (max-width: 992px) {
  .pd-gallery {
    aspect-ratio: 4 / 3;
  }
}
</style>
